import { useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { StringUtils, QueryUtils, QuestionUtils, BrowserUtils, StorageUtils, fromBase64CompressJson } from '../utils/siteUtils'
import { getFromBrowserStorage } from '../utils/storeUtils'
import { useGlobalState } from './GlobalStateProvider'
import { useEdgeGetOffer, useEdgeGetVehicleDetails } from './useEdgeDataFetcher'
import useGetElementHeight from './useGetElementHeight'

const initialwidth = BrowserUtils.isBrowser ? window.innerWidth : 10000
const defaultFunnelCode = 'kbb'
let experienceState = null
let qxData = null
let offerIdParam = null
let offerDealerId = null
let offerGroupId = null
let height = 0
let step = undefined

const shouldBypassEmitPageSizeEvent = () => {
  if (
    document.getElementById('WebComponentContainer-condition') == null &&
    document.getElementById('WebComponentContainer-contact') == null &&
    document.getElementById('WebComponentContainer-certificate') == null &&
    document.getElementById('WebComponentContainer-imagecapture') == null &&
    document.getElementById('WebComponentContainer-vehicleDetails') == null
  ) {
    return true
  }
  return false
}

const useExperienceStateHook = (experienceState, location) => {
  const urlState = StringUtils.getUrlState(location)
  const newStep = urlState && urlState.p
  let total = BrowserUtils.calculateHeaderAndFooter()
  let extraHeight = experienceState.isAdmin ? 100 : total

  const [newHeight] = useGetElementHeight('BodyDivFlex', 'body', extraHeight)

  useEffect(() => {
    if (height !== newHeight || (step !== undefined && step !== null && step !== newStep)) {
      height = newHeight

      const payload = { height, initial: step !== undefined && step !== null && step !== newStep }
      if (shouldBypassEmitPageSizeEvent()) {
        BrowserUtils.postMessage(JSON.stringify(payload), '*')
        BrowserUtils.postMessage(JSON.stringify({ action: 'getFrameDetails' }), '*')
      }
    }
    step = newStep
  }, [newHeight, experienceState.isAdmin, experienceState.isDigitalRetail])
}

export default (location, pageContext) => {
  const { globalState, updateGlobalState, globalVariables, updateGlobalVariables } = useGlobalState()
  const offerSubmitLink = location.state && location.state.offer && location.state.offer.offerId === globalState.offerIdSubmit
  const locationOffer = offerSubmitLink && location.state.offer
  const initUrl = StringUtils.getUrlState(location) || {}
  const prospectIdParam = initUrl.pa && initUrl.pa[0] && initUrl.pa[0].indexOf('prospect') === 0 && initUrl.pa[1]
  let offerResult = undefined
  let prospectResult = undefined
  const isOutageError = globalState && globalState.isOutageError
  const propspectId = globalState && globalState.prospectId
  const [offer, offerError, offerIsFetchingData] = isOutageError || prospectIdParam ? useEdgeGetVehicleDetails(prospectIdParam || propspectId) : useEdgeGetOffer(!locationOffer && offerIdParam)
  const offerIsFetching = offerIsFetchingData || (!offerIsFetchingData && !(offerError || []).length && !offer && (location.pathname.includes('/reviewoffer') || location.pathname.includes('/offer')))
  if (isOutageError || prospectIdParam) {
    prospectResult = offer
  } else {
    offerResult = offer
  }

  offerDealerId = offerResult && offerResult.prospect && offerResult.prospect.dealerId
  offerGroupId = offerResult && offerResult.prospect && offerResult.prospect.cobranding.groupId

  const fromAuction = offerResult?.status?.toLowerCase() === 'auction'
  let isReset = false // this reset idea is prone to errors
  // Previously we were resetting the experienceState if experienceState.dealerId was falsy (empty or 0) but offerDealerId had a value
  // Auction dealers have a 0 for their dealerId which put us in a rerender loop because assigned offers have an offerDealerId value
  const onlyOneDealerIdEmpty = !experienceState?.dealerId && offerDealerId && !fromAuction
  if (offerResult && (onlyOneDealerIdEmpty || (!experienceState?.groupId && offerGroupId))) {
    experienceState = {}
    isReset = true
  }

  if (!isReset && experienceState && Object.keys(experienceState).length && process.env.BUILD_STAGE !== 'build-html') {
    const { initialPathParams, initialQueryParams } = BrowserUtils.getInitParams()

    location.initQueryParams = initialQueryParams
    location.initPathParams = initialPathParams
  } else {
    StringUtils.getPathParameters(location.pathname)

    const initUrlState = StringUtils.getUrlState(location) || {}
    const initPathParams = initUrlState.pa || [] // this URL parsing will run once only initially and it is important to get path params on each refresh
    const initQueryParams = QueryUtils.getQueryParameters(location) || {}

    location.initPathParams = initPathParams
    location.initQueryParams = initQueryParams
    location.initUrlState = initUrlState

    const queryObject = initQueryParams

    queryObject.funnelcode = process.env.BUILD_STAGE !== 'build-html' ? queryObject.funnelcode : pageContext.experience

    if (location.href && (location.href.toLowerCase().includes('autotrader') || location.href.toLowerCase().includes('alblf-atc'))) {
      queryObject.funnelcode = 'atc'
    }

    queryObject.funnelcode = queryObject.funnelcode && queryObject.funnelcode.toLowerCase()
    queryObject.offercode = queryObject.offercode && queryObject.offercode.toLowerCase()

    // TODO: the whole offercode param detection, etc. should be reviewed / tested
    if (!queryObject.offercode && initPathParams[0] && initPathParams[0].length <= 3) {
      queryObject.offercode = initPathParams[0]
    }

    const urlDealerParams = parseUrlParameters(initPathParams)
    const fd = queryObject.fd
    let dealerInfo = {}
    let customerInfo = {}
    let useDealerZip = false

    if (fd) {
      const decoded = fromBase64CompressJson(fd)
      if (decoded) {
        dealerInfo = { dealerRepId: decoded.D, isConsumerOffer: decoded.C || false, showCustOption: decoded.O || false, sendEmail: decoded.S || false }
        customerInfo = { firstName: decoded.F, lastName: decoded.L, email: decoded.E, phone: decoded.P, zip: decoded.Z }
        useDealerZip = !!decoded.U
        if (decoded.vin) {
          updateGlobalState({ vinNo: decoded.vin })
        }
      }
    }

    // dealer guid can be changed/set by dealer id and group id
    const offerCode = (queryObject.offercode || (urlDealerParams && urlDealerParams.offerCode) || '').toLowerCase()
    const dealerId = queryObject.dealerid || queryObject.dealer_id || queryObject.siteref || (urlDealerParams && urlDealerParams.dealerId) || queryObject.dealerguid || offerDealerId
    const dealerIsDirectToKBB = queryObject.dealerIsDirectToKBB
    const groupId = queryObject.groupid || offerGroupId
    const dealerGuid = queryObject.dealerguid || (urlDealerParams && urlDealerParams.dealerGuid)
    const dealerInviteId = queryObject.inviteid
    const dealerPickupSVOCID = queryObject.svocid
    const showVechileImageOnLandingPage = queryObject.landingimage
    const offerCodesThatCanIncludeRepNameInUrl = ['c1', 'c2', 'c3', 'di']
    let dealerRepName = ''
    const lnxCode = queryObject.lnx
    const isVehicleGrounded = queryObject.isvehiclegrounded === 'true'
    const sessionId = queryObject.sessionid
    const customId = queryObject.customid ?? ''
    const urlTheme = queryObject.theme ?? ''

    //we should only try and get dealer rep name from url if we are working with the offer codes in offerCodesThatCanIncludeRepNameInUrl
    if (offerCodesThatCanIncludeRepNameInUrl.includes(offerCode)) {
      dealerRepName = queryObject.user
    }

    // Funnel Ownership
    const funnelcode = queryObject.funnelcode
    const isAdmin = !!queryObject.dat || !!queryObject.authtoken
    const isAdminInitMode = !!queryObject.dat && !queryObject.fd && !queryObject.offer // not fully passed info yet
    const experience = funnelcode === 'atc' ? 'atc' : funnelcode === 'kbb' ? 'kbb' : defaultFunnelCode
    const authToken = queryObject.authtoken

    // Functionality Changes
    const isDigitalRetail = !isAdmin && offerCode === 'd' // TODO: change styling based on this - offer page footer, rv not shown
    const isPrefilled = !!(((queryObject.year && queryObject.make) || queryObject.makemodel) && queryObject.model) // Atc ads and kbb ownership?
    const isCombinedOffer = queryObject.iscombined === 'true' && !isAdmin
    const isAssigned = dealerId
    const isCobrand = groupId && (offerCode === 'oc' || offerCode === 'wc' || offerCode === 'tc')
    const displayImages = experience === 'kbb' && !isDigitalRetail && !isAssigned && !isCobrand && !isAdmin // can only show evox images on kbb.com
    const queryVariation = queryObject.variation // allow passing optimizely experience as query param
    const siteRef = queryObject.siteref
    const headerType = setHeader(funnelcode, offerCode, isAdmin, isCombinedOffer, siteRef)
    const includePixall = !isAdmin && !['dmb', 'i', 'k', '#d'].includes(offerCode)
    const isUIOffForDR = offerCode === 'd' ? true : false
    const skipPhotoUpload = queryObject.lnx && queryObject.lnx.toLowerCase() === 'kbbhubownfirst' // Skip photo upload screen for LNX=KBBHUBOWNFIRST
    const isUIForWC = offerCode === 'wc'
    const isShowCustomerDealerList = !isDigitalRetail
    const isUIForTC = offerCode === 'tc'
    const isUIForOC = offerCode === 'oc'
    const isConditionQuizComponentEnabled = globalState.isConditionQuizComponentEnabled
    const isConsumerContactComponentEnabledForFury = globalState.isConsumerContactComponentEnabledForFury
    const isIneligibleVehiclesEnabled = globalState.isIneligibleVehiclesEnabled
    const landingTypeFromUrl = getLandingTypeFromUrl({ isAdmin, isDigitalRetail, isUIForWC, isUIForOC, isUIForTC, isAssigned })

    offerIdParam = (initPathParams[0] && initPathParams[0].indexOf('offer') === 0 && initPathParams[1]) || initQueryParams.offer
    // Set the Landing Type in sessionStorage for use in the Landing Page Redesign
    const generatedLandingType = BrowserUtils.isBrowser ? sessionStorage.getItem('GeneratedLandingType') : ''
    if (generatedLandingType && landingTypeFromUrl !== generatedLandingType) {
      sessionStorage.setItem('GeneratedLandingType', landingTypeFromUrl)
    }
    experienceState = {
      experience,
      headerType,
      isAdmin,
      isAdminInitMode,
      isCobrand,
      isAssigned,
      isPrefilled,
      isCombinedOffer,
      isDigitalRetail,
      offerCode: convertOfferCode(initialwidth < 768, offerCode, experience, isAssigned, isAdmin),
      dealerId,
      dealerRepName,
      dealerInviteId,
      dealerPickupSVOCID,
      groupId,
      customerInfo,
      dealerInfo,
      dealerGuid,
      acceptsRV: offerCode !== 'oc' && offerCode !== 'tc',
      mutateExperienceForNewOffer,
      displayImages,
      queryVariation,
      includePixall,
      cookieZip: BrowserUtils.isBrowser ? getFromBrowserStorage('ManualZipCode', false)?.ManualZipCode && getFromBrowserStorage('ZipCode', false)?.ZipCode : null,
      isUIOffForDR,
      showVechileImageOnLandingPage,
      dealerIsDirectToKBB,
      skipPhotoUpload,
      isUIForWC,
      isShowCustomerDealerList,
      lnxCode,
      isConditionQuizComponentEnabled,
      isConsumerContactComponentEnabledForFury,
      isIneligibleVehiclesEnabled,
      optimizelyData: pageContext['optimizelyData'],
      landingTypeFromUrl,
      isVehicleGrounded,
      prospectResult,
      sessionId,
      useDealerZip,
      offerId: offerIdParam,
      authToken,
      customId,
      urlTheme
    }

    if (isAdmin) {
      StorageUtils.clearProspect(offerIdParam)
    }

    const traceId = uuidv4()

    updateGlobalVariables({ traceId, authToken, refreshToken: false }) // set some variables as global variables immediately to be used by whole app

    if (BrowserUtils.isBrowser) {
      window.traceId = traceId // is this used somewhere else that we set a window variable???
      const qx = QuestionUtils.getQx({ ...initQueryParams, incomingUrl: window.location.href }, {})
      qxData = updateQx(qx, experienceState)
      window.setAuthToken = authToken => authToken && updateGlobalVariables({ authToken, refreshToken: false })
    }

    if (isAdminInitMode) {
      console.log('initializing...')
    }

    if (process.env.BUILD_STAGE !== 'build-html' && process.env.ICO_ENV !== 'prod') {
      console.log({ initPathParams, initQueryParams, initUrlState, experienceState })
    }
  }

  experienceState = { ...experienceState, prospectResult }
  useExperienceStateHook(experienceState, location)
  return { experienceState, qxData, globalState, globalVariables, updateGlobalState, updateGlobalVariables, offerGot: offerResult, offerError, offerIsFetching }
}

const getLandingTypeFromUrl = ({ isAdmin, isDigitalRetail, isUIForWC, isUIForOC, isUIForTC, isAssigned }) => {
  const isLandingDr = !isAdmin && (isDigitalRetail || isUIForWC || isUIForTC || isUIForOC)
  const isLandingAssigned = !isLandingDr && isAssigned
  const isLandingUnassigned = !isAdmin && !isLandingAssigned && !isLandingDr
  if (isLandingDr) {
    return 'dr'
  } else if (isLandingAssigned) {
    return 'assigned'
  } else if (isLandingUnassigned) {
    return 'unassigned'
  } else {
    return 'unassigned'
  }
}

const updateQx = (qx, experienceState) => {
  const values = [
    {
      questionId: 'qx/SiteReferralUserID',
      value: experienceState.dealerGuid || '00000000-0000-0000-0000-000000000000'
    },
    {
      questionId: 'qx/ConsumerGenerated',
      value: !experienceState.isAdmin
    },
    {
      questionId: 'qx/OfferCode',
      value: experienceState.offerCode ? experienceState.offerCode.toUpperCase() : ''
    },
    {
      questionId: 'qx/UserComments',
      value: experienceState.usercomments ? experienceState.usercomments : ''
    },
    {
      questionId: 'qx/ConsumerCommunicationMethod',
      value: experienceState && experienceState.dealerInfo && experienceState.dealerInfo.sendEmail ? 'Both' : ''
    },
    {
      questionId: 'qx/PixAllID',
      value: ''
    },
    {
      questionId: 'qx/PixAllRealID',
      value: ''
    },
    {
      questionId: 'qx/AuctionQuestionAnsweredByUser',
      value: ''
    },
    {
      questionId: 'qx/AreQuestionsVerified',
      value: ''
    },
    {
      questionId: 'qx/VerifiedMileage',
      value: ''
    },
    {
      questionId: 'qx/AnswerSourcesInput',
      value: [{ questionCode: 'DealerModifiedAccidentQuestion', value: false }]
    }
  ]

  values.forEach(item => {
    if (qx && !qx.find(question => question.questionId === item.questionId)) qx.push(item)
  })

  return qx
}

const mutateExperienceForNewOffer = (dealerInfoParams, customerInfoParams) => {
  StorageUtils.clearProspect()
  BrowserUtils.removeInitQueryParam('offer')
  BrowserUtils.removeInitPathParam('reviewoffer')
  BrowserUtils.removeInitPathParam('review')
  BrowserUtils.removeInitPathParam('offer')
  BrowserUtils.removeInitPathParam('inviteid')

  offerIdParam = null
  const { dealerInfo, customerInfo } = experienceState
  const copyParams = (oldParams, newParams) => Object.keys(newParams).forEach(key => (oldParams[key] = newParams[key]))

  copyParams(dealerInfo, dealerInfoParams)
  copyParams(customerInfo, customerInfoParams)

  const { dealerRepId: D, isConsumerOffer: C, sendEmail: S, showCustOption: O } = dealerInfo
  const { firstName: F, lastName: L, email: E, phone: P } = customerInfo
  const fdJSON = { D, C, S, O, F, L, E, P }
  const fd = window.btoa(JSON.stringify(fdJSON))

  BrowserUtils.updateInitQueryParam('fd', fd)
}

const convertOfferCode = (isMobile, OfferCode, funnelCode, isAssigned, isAdmin) => {
  let modifiedOfferCode = OfferCode

  if (modifiedOfferCode === 'sl') {
    return modifiedOfferCode
  }

  if (!isAdmin) {
    if (!modifiedOfferCode && funnelCode === 'kbb') {
      modifiedOfferCode = isAssigned ? 'kbi' : 'b'
    } else if (!modifiedOfferCode && isAssigned) {
      modifiedOfferCode = 'a'
    }
  } else {
    modifiedOfferCode = ''
  }

  if (isMobile) {
    if (modifiedOfferCode) {
      switch (modifiedOfferCode) {
        case 'b':
        case 'n':
          modifiedOfferCode = 'kbm'
          break
        case 'a':
          modifiedOfferCode = 'cml'
          break
        case 'w':
          modifiedOfferCode = 'cmw'
          break
        case 'vrf':
          modifiedOfferCode = 'vrm'
          break
        default:
          break
      }
    } else {
      if (isAdmin) {
        modifiedOfferCode = 'dmb'
      } else {
        modifiedOfferCode = 'cmb'
      }
    }
  }

  return modifiedOfferCode
}

const setHeader = (funnelcode, offerCode, isAdmin, isCombinedOffer, siteRef) => {
  if (isAdmin || (isCombinedOffer && BrowserUtils.isBrowser && BrowserUtils.isIframe())) {
    return 'noHeader'
  }

  const defaultCode = funnelcode || defaultFunnelCode

  if (offerCode) {
    switch (offerCode) {
      case 'di': // dealer invite website
      case 'w': // dealer website
      case 'i': // ipad kiosk
      case 'k': // kiosk
      case 'cmw':
      case 'dmb':
      case 'c1': // campaign
      case 'c2': // campaign
      case 'c3': // campaign
      case 'si': // service invites
        return 'kbbBranding'
      case 'd': // digital retail
        return 'noHeader'
      case 'oc': // unassigned cobranded offer code
      case 'wc': // assigned cobranded offer code
      case 'tc': // TaaS offer code
        return 'noHeader' // no header/footer
      case 'a':
      case 'cml':
      case 'cmb':
        return 'atc'
      default:
        return defaultCode
    }
  } else if (!!siteRef) {
    return 'kbbBranding'
  } else {
    return defaultCode
  }
}

const parseUrlParameters = urlParams => {
  function arrayContains(item, arr) {
    return arr.indexOf(item) > -1
  }

  const validCodes = ['w', 'k', 'i', 'di', 'c1', 'c2', 'c3', 'si', 'wc', 'oc', 'tc']
  const notOfferPage = urlParams[0] !== 'offer'
  const offerCode = notOfferPage && urlParams && urlParams.find(i => arrayContains(i.toLowerCase(), validCodes))
  const dealerId = notOfferPage && offerCode && urlParams && urlParams[1]
  const dealerGuid = notOfferPage && offerCode && urlParams && urlParams[2]

  return { offerCode, dealerId, dealerGuid }
}
